/**
 * ako-invoice-sync.js (full)
 *
 * What it does:
 * - Sync drawer inputs + Ako cart inputs -> cart.attributes (dedup + serialized)
 * - "Apply" button in drawer enforces intent (sets invoice_type=company when any value filled)
 * - On cart page, if intent is company, it will auto-switch Ako invoice type UI to company
 *   BUT: only ONCE per page load, delayed until Ako UI is stable.
 *
 * Requirements:
 * - Drawer Apply button must have id="AkoInvoiceApply"
 */

(function () {
  const ATTR = {
    invoiceType: "ako_invoice_type",
    companyName: "ako_company_name",
    companyCode: "ako_company_code",
  };

  const SEL = {
    drawerName: "#DrawerAkoCompanyName",
    drawerCode: "#DrawerAkoCompanyCode",

    akoName: 'input[name="ako-company-name"]',
    akoCode: 'input[name="ako-company-code"]',

    // Ako type menu item (cart page)
    akoCompanyTypeItem: '.ako-menu__item[data-invoice="company"]',

    applyBtn: "#AkoInvoiceApply",

    checkoutBtn: "#CartDrawer-Checkout",
    drawerForm: "#CartDrawer-Form",
  };

  // ===== State =====
  let lastEditAt = 0;
  let pending = null; // { until }

  // Serialized save control
  let inFlightPromise = null;
  let queuedAttrs = null;
  let lastSavedKey = "";

  // prevent double checkout submit
  let isSubmittingCheckout = false;

  // prevent type click spam
  let lastAutoSelectAt = 0;

  // NEW: cart page one-shot switch guard (per page load)
  let cartTypeSwitchedOnce = false;

  function $(s) {
    return document.querySelector(s);
  }

  function els() {
    return {
      drawerName: $(SEL.drawerName),
      drawerCode: $(SEL.drawerCode),
      akoName: $(SEL.akoName),
      akoCode: $(SEL.akoCode),
      akoCompanyTypeItem: $(SEL.akoCompanyTypeItem),
      applyBtn: $(SEL.applyBtn),
      form: $(SEL.drawerForm),
    };
  }

  function normName(v) {
    return (v ?? "").toString().trim();
  }

  function normCode(v) {
    return (v ?? "").toString().replace(/\D/g, "").slice(0, 8);
  }

  function setValueIfNotFocused(el, val) {
    if (!el) return;
    if (document.activeElement === el) return;
    const v = (val ?? "").toString();
    if (el.value !== v) el.value = v;
  }

  function hasCompanyIntentFromValues(name, code) {
    return !!(name || code);
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    return res.json();
  }

  function payloadKey(attrs) {
    return `${attrs[ATTR.companyName] || ""}|${attrs[ATTR.companyCode] || ""}|${attrs[ATTR.invoiceType] || ""}`;
  }

  function buildAttrs(name, code, forceCompanyType) {
    const hasAny = hasCompanyIntentFromValues(name, code);
    return {
      [ATTR.companyName]: name,
      [ATTR.companyCode]: code,
      // data layer intent: company when any field filled
      [ATTR.invoiceType]: forceCompanyType ? (hasAny ? "company" : "") : (hasAny ? "company" : ""),
    };
  }

  async function postUpdate(attrs) {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ attributes: attrs }),
    });
  }

  async function saveAttributesSerialized(attrs) {
    const key = payloadKey(attrs);

    // Dedup
    if (key === lastSavedKey) return;

    // serialize: latest wins
    if (inFlightPromise) {
      queuedAttrs = attrs;
      return inFlightPromise;
    }

    pending = { until: Date.now() + 1800 };

    inFlightPromise = (async () => {
      try {
        await postUpdate(attrs);
        lastSavedKey = key;
      } finally {
        // no-op
      }
    })();

    try {
      await inFlightPromise;
    } finally {
      inFlightPromise = null;
      pending = null;

      if (queuedAttrs) {
        const next = queuedAttrs;
        queuedAttrs = null;
        await saveAttributesSerialized(next);
      }
    }
  }

  // ===== Ako UI helpers =====
  function isCompanySelected(item) {
    if (!item) return false;
    return item.querySelector(".ako-selected-icon") != null;
  }

  function autoSelectCompanyTypeSafely() {
    const { akoCompanyTypeItem } = els();
    if (!akoCompanyTypeItem) return false;

    if (isCompanySelected(akoCompanyTypeItem)) return true;

    const now = Date.now();
    if (now - lastAutoSelectAt < 2000) return false; // throttle a bit more conservative
    lastAutoSelectAt = now;

    try {
      akoCompanyTypeItem.click();
      return true;
    } catch (e) {
      return false;
    }
  }

  // ===== Hydrate values only (no type click here) =====
  async function hydrateValuesOnly() {
    if (Date.now() - lastEditAt < 600) return;
    if (pending && Date.now() < pending.until) return;
    if (inFlightPromise) return;

    const cart = await fetchCart();
    const a = cart.attributes || {};
    const name = normName(a[ATTR.companyName]);
    const code = normCode(a[ATTR.companyCode]);

    const { drawerName, drawerCode, akoName, akoCode } = els();

    setValueIfNotFocused(drawerName, name);
    setValueIfNotFocused(drawerCode, code);
    setValueIfNotFocused(akoName, name);
    setValueIfNotFocused(akoCode, code);
  }

  // ===== NEW: Cart page auto switch type ONCE (delayed & stable) =====
  function looksLikeCartPage() {
    // any of these implies cart page context
    return !!document.querySelector(".ako-invoice-setting") || location.pathname === "/cart";
  }

  async function shouldSwitchTypeToCompany() {
    // decide by cart attributes (source of truth)
    const cart = await fetchCart();
    const a = cart.attributes || {};
    const type = (a[ATTR.invoiceType] ?? "").toString().trim();
    const name = normName(a[ATTR.companyName]);
    const code = normCode(a[ATTR.companyCode]);

    // If invoiceType already company OR user has any company values, treat as intent.
    // (Because you said customer may fill name/code but invoice type not reflected by Ako)
    return type === "company" || hasCompanyIntentFromValues(name, code);
  }

  async function ensureCompanyTypeOnCartPageOnce() {
    if (cartTypeSwitchedOnce) return;
    if (!looksLikeCartPage()) return;

    // Wait until Ako UI appears and stabilizes (max ~6s)
    const start = Date.now();
    const maxWait = 6000;

    // Only switch if intent says company
    let intentCompany = false;
    try {
      intentCompany = await shouldSwitchTypeToCompany();
    } catch (e) {
      return;
    }
    if (!intentCompany) return;

    // Poll until the company item exists and seems stable
    while (Date.now() - start < maxWait) {
      const item = document.querySelector(SEL.akoCompanyTypeItem);
      if (item) {
        // If already selected, mark done
        if (isCompanySelected(item)) {
          cartTypeSwitchedOnce = true;
          return;
        }

        // Extra stability delay: wait 500ms to let Ako finish its own init
        await new Promise((r) => setTimeout(r, 500));

        // Re-check after delay
        const item2 = document.querySelector(SEL.akoCompanyTypeItem);
        if (!item2) {
          await new Promise((r) => setTimeout(r, 250));
          continue;
        }
        if (isCompanySelected(item2)) {
          cartTypeSwitchedOnce = true;
          return;
        }

        // Do ONE click attempt, then stop (avoid tug-of-war)
        autoSelectCompanyTypeSafely();
        cartTypeSwitchedOnce = true;
        return;
      }

      await new Promise((r) => setTimeout(r, 250));
    }
  }

  // ===== Save from input changes (no auto type click here) =====
  function bindOnce(el, key, fn) {
    if (!el) return;
    if (el.dataset[key] === "1") return;
    el.dataset[key] = "1";
    fn(el);
  }

  async function saveFrom(source) {
    if (Date.now() - lastEditAt < 80) return;

    const { drawerName, drawerCode, akoName, akoCode } = els();

    const name =
      source === "drawer" ? normName(drawerName?.value) : normName(akoName?.value);

    const code =
      source === "drawer" ? normCode(drawerCode?.value) : normCode(akoCode?.value);

    setValueIfNotFocused(source === "drawer" ? akoName : drawerName, name);
    setValueIfNotFocused(source === "drawer" ? akoCode : drawerCode, code);

    const attrs = buildAttrs(name, code, false);
    await saveAttributesSerialized(attrs);

    try {
      await hydrateValuesOnly();
    } catch (e) {}
  }

  function bindInputEvents() {
    const { drawerName, drawerCode, akoName, akoCode } = els();

    const markEdit = () => {
      lastEditAt = Date.now();
    };

    bindOnce(drawerName, "akoBound", (x) => {
      x.addEventListener("input", markEdit);
      x.addEventListener("change", () => saveFrom("drawer"));
    });

    bindOnce(drawerCode, "akoBound", (x) => {
      x.addEventListener("input", markEdit);
      x.addEventListener("change", () => saveFrom("drawer"));
    });

    bindOnce(akoName, "akoBound", (x) => {
      x.addEventListener("input", markEdit);
      x.addEventListener("change", () => saveFrom("ako"));
    });

    bindOnce(akoCode, "akoBound", (x) => {
      x.addEventListener("input", markEdit);
      x.addEventListener("change", () => saveFrom("ako"));
    });
  }

  // ===== Apply button: enforce company intent + (if cart page) try click company now =====
  async function applyFromDrawerButton() {
    const { drawerName, drawerCode } = els();
    const name = normName(drawerName?.value);
    const code = normCode(drawerCode?.value);

    const attrs = buildAttrs(name, code, true);
    await saveAttributesSerialized(attrs);

    // Only after explicit user action:
    // - if user filled anything, we attempt to select company UI (if exists)
    // - and also mark cart page one-shot to run (in case UI appears later)
    if (hasCompanyIntentFromValues(name, code)) {
      autoSelectCompanyTypeSafely();
      // If user later goes to cart page in same session, allow one-shot ensure there too
      cartTypeSwitchedOnce = false;
    }
  }

  function bindApplyButton() {
    const { applyBtn } = els();
    if (!applyBtn) return;

    if (applyBtn.dataset.akoApplyBound === "1") return;
    applyBtn.dataset.akoApplyBound = "1";

    applyBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await applyFromDrawerButton();
      } catch (err) {
        console.warn("[ako-invoice-sync] apply failed:", err);
      }
    });
  }

  // ===== Checkout guard =====
  function bindCheckoutGuard() {
    document.addEventListener(
      "click",
      async (e) => {
        const btn = e.target.closest(SEL.checkoutBtn);
        if (!btn) return;

        if (isSubmittingCheckout) return;

        const { drawerName, drawerCode, form } = els();
        const name = normName(drawerName?.value);
        const code = normCode(drawerCode?.value);

        if (!name && !code) return;

        e.preventDefault();
        e.stopPropagation();
        isSubmittingCheckout = true;

        try {
          const attrs = buildAttrs(name, code, true);
          await saveAttributesSerialized(attrs);

          // explicit action: if cart page, one safe click
          if (hasCompanyIntentFromValues(name, code)) {
            autoSelectCompanyTypeSafely();
          }
        } catch (err) {
          console.warn("[ako-invoice-sync] checkout save failed:", err);
        }

        form?.submit();
      },
      true
    );
  }

  function observeLateMount() {
    const obs = new MutationObserver(() => {
      bindInputEvents();
      bindApplyButton();

      if (observeLateMount._t) return;
      observeLateMount._t = setTimeout(async () => {
        observeLateMount._t = null;
        try {
          await hydrateValuesOnly();
          // cart page one-shot ensure (safe): will only click once per page load
          await ensureCompanyTypeOnCartPageOnce();
        } catch (e) {}
      }, 450);
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener("DOMContentLoaded", async () => {
    try {
      await hydrateValuesOnly();
      await ensureCompanyTypeOnCartPageOnce(); // try once on load (delayed inside)
    } catch (e) {}

    bindInputEvents();
    bindApplyButton();
    bindCheckoutGuard();
    observeLateMount();
  });
})();
